import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory, Redirect } from "react-router-dom";
import {
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
  Alert,
  IconButton,
  Tooltip
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import moment from "moment";
import {
  sendAppointmentInvite,
  sendAppointmentCancelNotice
} from "../../../services/appointments";
import { momentLocalizer } from "react-big-calendar";
import IntroContainer from "../IntroContainer/IntroContainer";
import DeviceSelectionScreen from "./DeviceSelectionScreen/DeviceSelectionScreen";
import {
  useAppointments,
  getAppointmentStatus,
  AppointmentStatus,
  DEFAULT_DURATION_MINUTES
} from "../../../hooks/useAppointments";
import { Appointment } from "../../../state/appointments";
import ConfirmDialog from "../../common/ConfirmDialog";
import { isDurationValid } from "../../common/DurationField";
import ConsultationsTab from "./ConsultationsTab";
import AppointmentDetailsDialog from "./AppointmentDetailsDialog";
import EmailInviteStep from "./EmailInviteStep";
import ScheduleForm from "./ScheduleForm";
import HistoryTab from "./HistoryTab";
import DaySchedulePreview from "./DaySchedulePreview";
import MediaErrorSnackbar from "./MediaErrorSnackbar/MediaErrorSnackbar";
import { useApiTwilio } from "../../../state/useApiTwilio/twilio-provider";
import { useUserState } from "../../../state/user";
import useFichaClinica from "../../../hooks/useFichaClinica";
import useVideoContext from "../../../hooks/useVideoContext/useVideoContext";
import { typography } from "../../../utils/typography";
import { nextFiveMinutes, resolveScheduledDateTime } from "../../../utils/date";
import { doctorTitle } from "../../../utils/doctor";
import { extractEmails, isValidEmail } from "../../../utils/email";
require("./tabs.css");

// The dashboard tabs and the schedule confirmation step are real routes so the
// browser back button returns to the previous tab/step instead of the home page.
// Order matches the Tabs below: Consultations / Schedule / History.
const VIDEO_TAB_PATHS = [
  "/video/consultations",
  "/video/schedule",
  "/video/history"
];
const VIDEO_CONFIRM_PATH = "/video/schedule/confirm";

const pageSx = { bgcolor: "#FCFCFF", minHeight: "100vh" };

const stickyBarSx = {
  bgcolor: "#FCFCFF",
  boxShadow: "0px 4px 6px rgba(30, 39, 79, 0.1)",
  position: "sticky",
  top: 80,
  zIndex: 10
};

const tabsSx = {
  "& .MuiTabs-indicator": {
    backgroundColor: "#0DBDA7",
    height: "2px"
  },
  minHeight: 47,
  px: { xs: "16px", sm: "24px" }
};

const tabSx = (active: boolean) => ({
  minHeight: 47,
  ...typography.body.m,
  fontWeight: active ? 600 : 400,
  color: active ? "#0DBDA7" : "#1E274F",
  textTransform: "none" as const,
  "&.Mui-selected": { color: "#0DBDA7" }
});

const containerSx = {
  pt: { xs: "24px", sm: "32px" },
  pb: { xs: "24px", sm: "48px" }
};

const scheduleLayoutSx = {
  display: "flex",
  gap: 4,
  alignItems: "flex-start",
  flexWrap: "wrap"
};

const previewColSx = { flex: 1, minWidth: 320 };

export default function CreatePreJoinScreens() {
  const { token, user } = useUserState();
  const { createRoom, loading: loadingTwilio, setNameUser } = useApiTwilio();
  const { getAudioAndVideoTracks } = useVideoContext();
  const { data: clinicalFiles } = useFichaClinica();
  const { t, i18n } = useTranslation();
  const location = useLocation<{
    autoStart?: boolean;
    patientName?: string;
    appointmentId?: string;
    externalId?: string;
  }>();
  const isPt = i18n.language.startsWith("pt");

  const localizer = useMemo(() => {
    moment.locale(isPt ? "pt" : "en-gb");
    return momentLocalizer(moment);
  }, [isPt]);

  const calendarMessages = useMemo(
    () => ({
      next: "›",
      previous: "‹",
      today: t("calendar.today"),
      month: t("calendar.month"),
      week: t("calendar.week"),
      day: t("calendar.day"),
      agenda: t("calendar.agenda"),
      date: t("calendar.date"),
      time: t("calendar.time"),
      event: t("calendar.event"),
      noEventsInRange: t("calendar.noEventsInRange"),
      showMore: (total: number) => t("calendar.showMore", { count: total })
    }),
    [t]
  );

  // Default scroll position for day/week views: focus one hour before "now"
  // so the previous hour, the current hour and what's coming are all in view
  // (full 24h still accessible via vertical scroll).
  const defaultScrollTime = useMemo(
    () =>
      moment()
        .subtract(1, "hour")
        .toDate(),
    []
  );

  const history = useHistory();
  // Tab + confirmation step are derived from the URL (see VIDEO_TAB_PATHS), so
  // the browser back button moves between them.
  const pathname = location.pathname;
  const emailStep = pathname === VIDEO_CONFIRM_PATH;
  const tabValue = pathname.startsWith("/video/schedule")
    ? 1
    : pathname.startsWith("/video/history")
    ? 2
    : 0;
  const [view, setView] = useState<"dashboard" | "prejoin">("dashboard");
  const {
    appointments,
    upcoming,
    past,
    addAppointment,
    deleteAppointment
  } = useAppointments();

  // patientName (lowercased) → patient number (nhsId). Appointments only carry the
  // patient's name, so the number is resolved from the doctor's clinical files —
  // the same way the pictures are.
  const patientNhsIdMap = useMemo<Record<string, string>>(() => {
    if (!clinicalFiles || !Array.isArray(clinicalFiles)) return {};
    return (clinicalFiles as any[]).reduce((acc, f) => {
      const name = (
        f.name ||
        f.patientName ||
        f.patient?.name ||
        ""
      ).toLowerCase();
      if (name && f.nhsId) acc[name] = f.nhsId;
      return acc;
    }, {} as Record<string, string>);
  }, [clinicalFiles]);

  // History filters
  const [historySearch, setHistorySearch] = useState("");
  const [historyDate, setHistoryDate] = useState<moment.Moment | null>(null);
  const [historyStatus, setHistoryStatus] = useState<AppointmentStatus | "">(
    ""
  );
  // past is already sorted desc (most recent first) by the hook
  const filteredPast = useMemo(
    () =>
      past.filter(a => {
        // Match on name or patient number, like the patients list.
        const query = historySearch.trim().toLowerCase();
        const nhsId = patientNhsIdMap[a.patientName.toLowerCase()] ?? "";
        const matchPatient =
          !query ||
          a.patientName.toLowerCase().includes(query) ||
          nhsId.toLowerCase().includes(query);
        const matchDate =
          !historyDate || moment(a.start).isSame(historyDate, "day");
        const matchStatus =
          !historyStatus || getAppointmentStatus(a) === historyStatus;
        return matchPatient && matchDate && matchStatus;
      }),
    [past, historySearch, historyDate, historyStatus, patientNhsIdMap]
  );

  const patientPictureMap = useMemo<Record<string, string>>(() => {
    if (!clinicalFiles || !Array.isArray(clinicalFiles)) return {};
    return (clinicalFiles as any[]).reduce((acc, f) => {
      const name = (
        f.name ||
        f.patientName ||
        f.patient?.name ||
        ""
      ).toLowerCase();
      if (name && f.pictureUrl) acc[name] = f.pictureUrl;
      return acc;
    }, {} as Record<string, string>);
  }, [clinicalFiles]);

  const [mediaError, setMediaError] = useState<Error | undefined>(undefined);
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);

  // Doctor's display name (with Dr./Dra. from gender), reused by the invite link
  // and the appointment details modal.
  const doctorDisplayName = useMemo(() => {
    const clinicalName = user?.clinicalName || user?.name || "";
    const title = doctorTitle(user?.personalInfo?.gender, t);
    return title && clinicalName ? `${title} ${clinicalName}` : clinicalName;
  }, [user, t]);

  // Shown as the participant name when the appointment has no patient name yet.
  const defaultPatientName = t("videoConsultations.defaultPatientName");

  // Builds the patient-facing room link from the appointment's room id, so it can
  // be shown/copied for a scheduled appointment without first creating the room.
  const buildInviteLink = useCallback(
    (patientNameParam: string, roomName?: string) => {
      if (!roomName) return "";
      const encodedPatient = encodeURIComponent(
        patientNameParam || defaultPatientName
      );
      const encodedDoctor = encodeURIComponent(doctorDisplayName);
      return `${window.location.origin}/invite-video/${roomName}/${encodedPatient}/${encodedDoctor}`;
    },
    [doctorDisplayName, defaultPatientName]
  );

  // Scheduling form
  const [patientName, setPatientName] = useState("");
  const [
    scheduledDateTime,
    setScheduledDateTime
  ] = useState<moment.Moment | null>(() => nextFiveMinutes());
  // Raw text so the field can be cleared while typing (see DurationField).
  const [duration, setDuration] = useState(String(DEFAULT_DURATION_MINUTES));
  const [notes, setNotes] = useState("");

  // The slot being booked, so the confirmation step can show it in the day
  // preview next to the appointments already scheduled for that day.
  const pendingSlot = useMemo(() => {
    if (!scheduledDateTime || !isDurationValid(duration)) return undefined;
    return {
      start: scheduledDateTime.toDate(),
      end: scheduledDateTime
        .clone()
        .add(parseInt(duration, 10), "minutes")
        .toDate(),
      patientName
    };
  }, [scheduledDateTime, duration, patientName]);

  const handleScheduledDateChange = useCallback(
    (value: moment.Moment | null) =>
      setScheduledDateTime(prev => resolveScheduledDateTime(prev, value)),
    []
  );

  // Cancellation confirmation
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(
    null
  );
  const [cancelling, setCancelling] = useState(false);

  // Email step (whether we're on it is derived from the URL — see emailStep above)
  const [emailList, setEmailList] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  // Appointments are oriented around the patient's clinical file. The contact
  // record carries `clinicalFileId` (distinct from its own `_id`).
  const getClinicalFileId = (obj: any): string | undefined =>
    obj?.clinicalFileId ?? obj?.fileId ?? obj?.scriboPartition;

  const findPatientByName = (name: string): any =>
    (Array.isArray(clinicalFiles) ? (clinicalFiles as any[]) : []).find(
      f =>
        (f.name || f.patientName || f.patient?.name || "").toLowerCase() ===
        name.trim().toLowerCase()
    );

  // Matching by clinical file id is more reliable than by name (no case /
  // accent / whitespace pitfalls) — used to resolve the clinical file on cancel.
  const findPatientByFileId = (fileId?: string | null): any =>
    fileId
      ? (Array.isArray(clinicalFiles) ? (clinicalFiles as any[]) : []).find(
          f => getClinicalFileId(f) === fileId
        )
      : null;

  // The picked patient must map to a clinical file in order to schedule.
  const selectedClinicalFileId = getClinicalFileId(
    findPatientByName(patientName)
  );
  const scheduleDisabled =
    !patientName.trim() ||
    !scheduledDateTime ||
    !selectedClinicalFileId ||
    !isDurationValid(duration);
  // The doctor typed a name that doesn't match any of their clinical files.
  const unknownPatient = !!patientName.trim() && !selectedClinicalFileId;

  const patientOptions: string[] = React.useMemo(() => {
    if (!clinicalFiles || !Array.isArray(clinicalFiles)) return [];
    // clinicalFilesAccesses can return several access rows per clinical file, so the
    // same patient name shows up more than once — dedupe to one option each.
    const names = (clinicalFiles as any[])
      .map((f: any) => f.name || f.patientName || f.patient?.name || "")
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [clinicalFiles]);

  const handleStartConsulta = useCallback(
    async (
      patientNameParam?: string,
      appointmentId?: string,
      externalIdParam?: string
    ) => {
      if (!token) return;

      const clinicalName = user?.clinicalName || user?.name || "";
      // Link the room to the appointment (by its externalId) so the backend can
      // move it to ON_GOING now and FINISHED when the room closes. Prefer the
      // externalId passed in (e.g. from the patients dialog navigation), falling
      // back to a lookup once the appointments list is available.
      const externalId =
        externalIdParam ??
        (appointmentId
          ? appointments.find(a => a.id === appointmentId)?.externalId
          : undefined);

      const result: any = await createRoom(token, {
        roomName: externalId,
        doctorName: clinicalName
      });
      if (!result?.roomName) return;

      setInviteLink(buildInviteLink(patientNameParam || "", result.roomName));
      setNameUser(patientNameParam || defaultPatientName);
      getAudioAndVideoTracks().catch((err: Error) => setMediaError(err));
      setView("prejoin");
    },
    [
      token,
      user,
      createRoom,
      getAudioAndVideoTracks,
      setNameUser,
      appointments,
      buildInviteLink,
      defaultPatientName
    ]
  );

  // Appointment details modal, kept in the URL (?appointment=<id>) so the
  // browser back button closes it — same pattern as the patient card.
  const selectedAppointmentId = new URLSearchParams(location.search).get(
    "appointment"
  );
  const selectedAppointment = selectedAppointmentId
    ? appointments.find(a => a.id === selectedAppointmentId) ?? null
    : null;

  const handleOpenDetails = (appointmentId: string) =>
    history.push({
      pathname: location.pathname,
      search: `?appointment=${appointmentId}`
    });

  const handleCloseDetails = () =>
    history.replace({ pathname: location.pathname, search: "" });

  const handleStartFromDetails = (appt: Appointment) => {
    handleCloseDetails();
    handleStartConsulta(appt.patientName, appt.id, appt.externalId);
  };

  // Auto-start if navigated from patient dialog
  useEffect(() => {
    if (location.state?.autoStart && location.state?.patientName) {
      handleStartConsulta(
        location.state.patientName,
        location.state.appointmentId,
        location.state.externalId
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoToEmailStep = () => {
    if (!patientName.trim() || !scheduledDateTime || !isDurationValid(duration))
      return;
    const patientObj = findPatientByName(patientName);
    if (!getClinicalFileId(patientObj)) {
      setEmailFeedback({ type: "error", msg: t("videoDialog.noClinicalFile") });
      return;
    }
    setEmailList(extractEmails(patientObj));
    setEmailFeedback(null);
    history.push(VIDEO_CONFIRM_PATH);
  };

  const handleAddEmail = () => {
    const val = newEmail.trim();
    // Empty is a no-op (the field may have already auto-committed on blur).
    if (!val) return;
    if (!isValidEmail(val)) {
      setEmailError(t("videoDialog.invalidEmail"));
      return;
    }
    if (!emailList.includes(val)) setEmailList(prev => [...prev, val]);
    setNewEmail("");
    setEmailError("");
  };

  const handleRemoveEmail = (email: string) =>
    setEmailList(prev => prev.filter(e => e !== email));

  const handleEmailBack = () => {
    history.push(VIDEO_TAB_PATHS[1]);
  };

  // Leaving the confirmation step — via the Back button, the browser back button,
  // or after a successful schedule — clears the transient email UI state.
  useEffect(() => {
    if (!emailStep) {
      setEmailError("");
      setEmailFeedback(null);
      setNewEmail("");
    }
  }, [emailStep]);

  const handleNewEmailChange = (value: string) => {
    setNewEmail(value);
    setEmailError("");
  };

  const handleConfirmSchedule = async () => {
    // Accept an email typed but not yet added via the "Add" button, so
    // the common single-recipient case needs no extra click.
    const pending = newEmail.trim();
    let emailsToSend = emailList;
    if (pending) {
      if (!isValidEmail(pending)) {
        setEmailError(t("videoDialog.invalidEmail"));
        return;
      }
      emailsToSend = emailList.includes(pending)
        ? emailList
        : [...emailList, pending];
    }
    if (emailsToSend.length === 0) {
      setEmailError(t("videoDialog.noEmails"));
      return;
    }
    if (!patientName.trim() || !scheduledDateTime || !isDurationValid(duration))
      return;
    const patientObj = findPatientByName(patientName);
    const clinicalFileId = getClinicalFileId(patientObj);
    if (!clinicalFileId) {
      setEmailFeedback({ type: "error", msg: t("videoDialog.noClinicalFile") });
      return;
    }
    setSending(true);
    try {
      await addAppointment({
        start: scheduledDateTime.toDate(),
        clinicalFileId,
        patientName: patientName.trim(),
        durationMinutes: parseInt(duration, 10),
        notes: notes.trim() || undefined
      });
    } catch {
      setEmailFeedback({ type: "error", msg: t("videoDialog.scheduleError") });
      setSending(false);
      return;
    }
    try {
      await sendAppointmentInvite(token!, {
        emails: emailsToSend,
        patientName: patientName.trim(),
        patientGender: patientObj?.gender,
        doctorName:
          (user as any)?.clinicalName ??
          (user as any)?.personalInfo?.givenName ??
          "",
        doctorGender: (user as any)?.personalInfo?.gender,
        scheduledDate: scheduledDateTime.format()
      });
      setEmailFeedback({ type: "success", msg: t("videoDialog.sendSuccess") });
    } catch {
      setEmailFeedback({ type: "error", msg: t("videoDialog.sendError") });
    } finally {
      setSending(false);
    }
    setTimeout(() => {
      setPatientName("");
      setScheduledDateTime(nextFiveMinutes());
      setDuration(String(DEFAULT_DURATION_MINUTES));
      setNotes("");
      setEmailList([]);
      // Leaving the confirm step clears the remaining email UI state (useEffect).
      history.push(VIDEO_TAB_PATHS[0]);
    }, 1800);
  };

  const handleSchedule = () => {
    handleGoToEmailStep();
  };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;
    // Capture the appointment before deleting — it leaves the lists afterwards.
    const appt = appointments.find(a => a.id === appointmentToCancel);
    setCancelling(true);
    try {
      await deleteAppointment(appointmentToCancel);
      // Notify the patient that the appointment was cancelled (mirrors the
      // invite sent on scheduling), pulling the emails from the same clinical file
      // source. A notify failure must not block the cancel.
      const patientObj = appt
        ? findPatientByFileId(appt.clinicalFileId) ??
          findPatientByName(appt.patientName)
        : null;
      const emails = extractEmails(patientObj);
      if (token && appt && emails.length > 0) {
        try {
          await sendAppointmentCancelNotice(token, {
            emails,
            patientName: appt.patientName,
            patientGender: patientObj?.gender,
            doctorName:
              (user as any)?.clinicalName ??
              (user as any)?.personalInfo?.givenName ??
              "",
            doctorGender: (user as any)?.personalInfo?.gender,
            // Same date format the (working) /notify uses — moment().format()
            // keeps the local offset (e.g. +01:00) rather than a UTC "Z".
            scheduledDate: moment(appt.start).format()
          });
        } catch {
          // cancellation already succeeded; ignore notify failure
        }
      }
      setAppointmentToCancel(null);
    } catch {
      // keep the dialog open so the doctor can retry
    } finally {
      setCancelling(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // ── Prejoin view (after creating room) ─────────────────────────────────────
  if (view === "prejoin") {
    return (
      <IntroContainer>
        {inviteLink && (
          <Box mb={3}>
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              action={
                <Tooltip
                  title={
                    copied
                      ? t("videoConsultations.copied")
                      : t("videoConsultations.copyLink")
                  }
                >
                  <IconButton
                    size="small"
                    onClick={handleCopyLink}
                    color="inherit"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <Typography variant="body2" fontWeight="bold">
                {t("videoConsultations.inviteLink")}
              </Typography>
              {copied && (
                <Typography variant="caption" color="success.dark">
                  {t("videoConsultations.linkCopied")}
                </Typography>
              )}
            </Alert>
          </Box>
        )}
        <MediaErrorSnackbar error={mediaError} />
        <DeviceSelectionScreen />
      </IntroContainer>
    );
  }

  // ── Dashboard (tabs) ────────────────────────────────────────────────────────
  // Canonicalise the bare /video URL to the first tab so the tab is always in
  // the address bar (shareable, reloadable, and back-navigable). Skip when we
  // arrived with autoStart state (from the patient dialog) — redirecting would
  // drop that state before the start-consultation effect can read it.
  if (
    (pathname === "/video" || pathname === "/video/") &&
    !location.state?.autoStart
  ) {
    return <Redirect to={VIDEO_TAB_PATHS[0]} />;
  }

  const upcomingAppointments = upcoming.slice(0, 4);

  return (
    <Box sx={pageSx}>
      {/* Sticky tab bar right below the 80px navbar */}
      <Box sx={stickyBarSx}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => history.push(VIDEO_TAB_PATHS[v])}
          className="dashboard-tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={tabsSx}
        >
          {[
            t("videoConsultations.tabConsultations"),
            t("videoConsultations.tabSchedule"),
            t("videoConsultations.tabHistory")
          ].map((label, i) => (
            <Tab key={i} label={label} sx={tabSx(tabValue === i)} />
          ))}
        </Tabs>
      </Box>

      <Container fixed maxWidth="xl" sx={containerSx}>
        {/* ── Tab 0: Consultas ── */}
        {tabValue === 0 && (
          <ConsultationsTab
            upcomingAppointments={upcomingAppointments}
            appointments={appointments}
            loading={loadingTwilio}
            onStart={handleStartConsulta}
            onCancel={setAppointmentToCancel}
            onOpenDetails={handleOpenDetails}
            pictureMap={patientPictureMap}
            localizer={localizer}
            calendarMessages={calendarMessages}
            defaultScrollTime={defaultScrollTime}
          />
        )}

        {/* ── Tab 1: Email step ── */}
        {tabValue === 1 && emailStep && (
          <Box sx={scheduleLayoutSx}>
            <EmailInviteStep
              scheduledDateTime={scheduledDateTime}
              patientName={patientName}
              emailList={emailList}
              newEmail={newEmail}
              emailError={emailError}
              feedback={emailFeedback}
              sending={sending}
              onBack={handleEmailBack}
              onNewEmailChange={handleNewEmailChange}
              onAddEmail={handleAddEmail}
              onRemoveEmail={handleRemoveEmail}
              onConfirm={handleConfirmSchedule}
            />
            <Box sx={previewColSx}>
              <DaySchedulePreview
                date={scheduledDateTime}
                appointments={appointments}
                localizer={localizer}
                defaultScrollTime={defaultScrollTime}
                pendingSlot={pendingSlot}
              />
            </Box>
          </Box>
        )}

        {/* ── Tab 1: Agendar ── */}
        {tabValue === 1 && !emailStep && (
          <Box sx={scheduleLayoutSx}>
            <ScheduleForm
              patientOptions={patientOptions}
              patientName={patientName}
              onPatientNameChange={setPatientName}
              scheduledDateTime={scheduledDateTime}
              onDateChange={handleScheduledDateChange}
              duration={duration}
              onDurationChange={setDuration}
              notes={notes}
              onNotesChange={setNotes}
              scheduleDisabled={scheduleDisabled}
              unknownPatient={unknownPatient}
              onSchedule={handleSchedule}
            />
            <Box sx={previewColSx}>
              <DaySchedulePreview
                date={scheduledDateTime}
                appointments={appointments}
                localizer={localizer}
                defaultScrollTime={defaultScrollTime}
              />
            </Box>
          </Box>
        )}

        {/* ── Tab 2: Histórico ── */}
        {tabValue === 2 && (
          <HistoryTab
            search={historySearch}
            onSearchChange={setHistorySearch}
            date={historyDate}
            onDateChange={setHistoryDate}
            status={historyStatus}
            onStatusChange={setHistoryStatus}
            rows={filteredPast}
            pictureMap={patientPictureMap}
            nhsIdMap={patientNhsIdMap}
          />
        )}
      </Container>

      <AppointmentDetailsDialog
        appointment={selectedAppointment}
        doctorName={doctorDisplayName}
        inviteLink={
          selectedAppointment
            ? buildInviteLink(
                selectedAppointment.patientName,
                selectedAppointment.externalId
              )
            : ""
        }
        starting={loadingTwilio}
        onClose={handleCloseDetails}
        onStart={handleStartFromDetails}
      />

      <ConfirmDialog
        open={!!appointmentToCancel}
        destructive
        loading={cancelling}
        title={t("videoConsultations.cancelTitle")}
        message={t("videoConsultations.cancelMessage")}
        confirmLabel={t("videoConsultations.cancelConfirm")}
        cancelLabel={t("videoConsultations.cancelDismiss")}
        onConfirm={handleConfirmCancel}
        onClose={() => !cancelling && setAppointmentToCancel(null)}
      />
    </Box>
  );
}
